import { PAGE_TYPE } from '@/app/common/pageType.ts';
import { DETAIL_ONLY_PARAMS, URLParameters } from '@/app/common/urlParameters.ts';
import { removeParameters } from '@/app/common/urlParametersUtils.ts';
import { BASE_PATH } from '@/app/components/search/utils/path.ts';
import { isDestinationsPageFromSearchParams } from '@/app/components/search/utils/searchTypesUtils.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { createQueryString } from '@/utils/queryUtils.ts';

/**
 * The results-page URL that the detail page's back control returns to.
 *
 * `handleBackToResults` already navigates client-side, so this exists for the *href* of the mobile
 * back arrow: it sits on the hero image, so it is on screen and tappable well before React has
 * hydrated, and until MPH-850 it carried a placeholder `/` href. A tap in that window is a plain
 * anchor navigation, and `/` resolves through the base path to a param-less `/s/tsx`, which the
 * middleware redirects to the brand homepage - the user lost their search instead of going back to
 * it. Rendering the real results URL makes the pre-hydration tap land on the same page the click
 * handler would have gone to, just as a full navigation.
 *
 * Built from the searchParams the page was rendered with (never `window.location`) so the server and
 * client markup agree during hydration.
 */
export const createBackToResultsUrl = ({
  searchParams,
  highlightHotelId,
  geodbLiveCountrySearch,
}: {
  searchParams: SearchParams;
  highlightHotelId?: number;
  geodbLiveCountrySearch: boolean;
}): string => {
  const pageType = isDestinationsPageFromSearchParams({ searchParams, geodbLiveCountrySearch })
    ? PAGE_TYPE.SEARCH_DESTINATIONS
    : PAGE_TYPE.SEARCH;

  const searchParamsResults = removeParameters(
    {
      ...searchParams,
      [URLParameters.PAGE_TYPE]: pageType,
      // Mirrors handleBackToResults' default of highlighting the hotel the user came from.
      ...(highlightHotelId ? { [URLParameters.HIGHLIGHT_HOTEL_ID]: `${highlightHotelId}` } : {}),
    },
    DETAIL_ONLY_PARAMS
  );

  return `${BASE_PATH}?${createQueryString(searchParamsResults)}`;
};
